import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FolderComponent } from '../../core/components/folder/folder.component';
import { FileUploadComponent } from '../../core/components/file-upload/file-upload.component';
import { StorageService } from '../../core/services/storage.service';
import { StorageUploadStateService } from '../../core/services/storage-upload-state.service';
import { TranslatePipe } from '@ngx-translate/core';
import { NavigationComponent } from '../../layout/navigation/navigation.component';
import { UserStateService } from '../../core/services/user.state.service';
import { StorageStateService } from '../../core/services/storage-state.service';
import { ScreenLoaderComponent } from '../../shared/UIKit/screen-loader/screen-loader.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceLoad } from '../../shared/UIKit/screen-loader/debounce-load';
import { DialogService } from '../../core/services/dialog.service';
import { FileUploadState } from '../../core/models/file-upload-state.model';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  GlobalPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';

@Component({
  selector: 'login-page',
  imports: [
    FormsModule,
    CommonModule,
    FolderComponent,
    TranslatePipe,
    NavigationComponent,
    ScreenLoaderComponent,
  ],
  templateUrl: './file-upload-list.component.html',
})
export class FileUploadListComponent implements OnInit, OnDestroy {
  public readonly folderId = input.required<string>();

  protected readonly storageStateService = inject(StorageStateService);
  protected readonly storageUploadStateService = inject(
    StorageUploadStateService,
  );
  protected readonly userStateService = inject(UserStateService);

  protected readonly screenLoader = new BehaviorSubject<boolean>(false);
  public loader = this.selectScreenLoaderVisible$();

  private overlayRef: OverlayRef | null = null;

  public constructor(
    private overlay: Overlay,
    private injector: Injector,
    private readonly storageService: StorageService,
    private readonly destroyRef: DestroyRef,
    private readonly dialogService: DialogService,
  ) {
    effect(() => {
      if (this.storageUploadStateService.isFileUploadVisible()) {
        if (!this.overlayRef) {
          this.openFileUploadOverlay();
        }
      } else {
        if (this.overlayRef) {
          this.closeFileUploadOverlay();
        }
      }
    });
  }

  ngOnInit() {
    this.storageStateService.folderId.set(this.folderId());
  }

  openFileUploadOverlay() {
    const positionStrategy: GlobalPositionStrategy = this.overlay
      .position()
      .global()
      .bottom('50px')
      .left('50px');

    const overlayConfig: OverlayConfig = new OverlayConfig({
      hasBackdrop: false,
      positionStrategy: positionStrategy,
    });

    this.overlayRef = this.overlay.create(overlayConfig);

    const fileUploadPortal = new ComponentPortal(
      FileUploadComponent,
      null,
      this.injector,
    );

    this.overlayRef.attach(fileUploadPortal);

    this.overlayRef
      .backdropClick()
      .subscribe(() => this.closeFileUploadOverlay());
  }

  closeFileUploadOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  uploadFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      return;
    }

    if (!this.isFileExtensionAllowed(files)) {
      this.dialogService.showError('Tipo di file non valido');
      return;
    }

    if (!this.isFileNameUnique(files)) {
      this.dialogService.showError(
        'Non è possibile caricare file con lo stesso nome',
      );
      return;
    }

    if (
      this.storageUploadStateService.fileUploadState() !==
        FileUploadState.COMPLETED &&
      this.storageUploadStateService.fileUploadState() !==
        FileUploadState.ERROR &&
      this.storageUploadStateService.fileUploadState() !== FileUploadState.READY
    ) {
      this.dialogService.showError(
        'Caricamento di un file in corso. Attendere',
      );
      return;
    }

    this.storageUploadStateService.startUploading(this.folderId(), files[0]);
  }

  private isFileExtensionAllowed(files: FileList): boolean {
    const folder = this.storageStateService.folder();
    if (!folder) {
      return false;
    }

    if (!folder.allowedFileFormats || folder.allowedFileFormats.trim() === '') {
      return true;
    }

    const allowedExtensions = this.extractAllowedFormats(
      folder.allowedFileFormats,
    );

    return Array.from(files).every((file) => {
      return allowedExtensions.has(file.type);
    });
  }

  private isFileNameUnique(files: FileList): boolean {
    const existingFiles = this.storageStateService.files();
    if (!existingFiles || !files || files.length === 0) {
      return true;
    }

    const existingFileNames = new Set<string>(
      existingFiles.map((file) => file.name),
    );

    for (let i = 0; i < files.length; i++) {
      if (existingFileNames.has(files[i].name)) {
        return false;
      }
    }
    return true;
  }

  private extractAllowedFormats(allowedFileFormats: string): Set<string> {
    return new Set(
      allowedFileFormats
        .split(',')
        .map((ext) => ext.trim().toLowerCase())
        .filter((ext) => ext.length > 0),
    );
  }

  public downloadAllFiles() {
    this.storageService.downloadAll(this.folderId()).subscribe({
      next: (blob: Blob) => {
        const folderName = this.storageStateService.folder().name;
        const downloadFileName = `${folderName}.zip`;
        this.storageService.triggerBrowserDownload(blob, downloadFileName);
        this.dialogService.showInfo(
          'Il download di ' +
            this.storageStateService.folder().name +
            ' inizierà a breve...',
        );
      },
      error: (err) => {
        console.error('Error downloading all files:', err);
      },
    });
  }

  private selectScreenLoaderVisible$(): Observable<boolean> {
    return this.screenLoader.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceLoad(250, 500),
    );
  }

  ngOnDestroy(): void {
    this.closeFileUploadOverlay();
  }
}
